import {
  ADD_ERROR_MESSAGE,
  CLEAR_ERROR_MESSAGE,
  SET_CONTEXT_PANEL_TURN,
  TOGGLE_CHAT_VISIBILITY,
  ADD_MESSAGE,
  ADD_BULK_MESSAGE,
  USER_TYPING,
  ASSISTANT_RESPONDING,
  DELETE_ALL_MESSAGES,
  DELETE_MESSAGES_KEEPING_INITIAL,
  SET_HAS_INITIALIZED_MESSAGES,
  ADD_SELECTED_VALUE,
  REMOVE_SELECTED_VALUE,
  REMOVE_ALL_SELECTED_VALUE,
  ADD_REPORT,
  LOAD_REPORTS,
  SET_ACTIVE_REPORT,
  APPEND_TO_LAST_MESSAGE,
  SET_CHAT_EXPANDED,
  SET_TASK_STATUS_MESSAGE,
  SHOW_CITATIONS,
  HIDE_CITATIONS,
  CLEAR_CITATIONS,
  SET_HISTORY_METADATA,
  CLEAR_HISTORY_METADATA,
  UPDATE_REPORT_MESSAGE,
  ENTER_SECTION_EDIT_MODE,
  EXIT_SECTION_EDIT_MODE,
  REVISE_SECTION_DRAFT_START,
  REVISE_SECTION_DRAFT_SUCCESS,
  REVISE_SECTION_DRAFT_FAILURE,
  SAVE_SECTION_EDIT_START,
  SAVE_SECTION_EDIT_SUCCESS,
  SAVE_SECTION_EDIT_FAILURE,
  DELETE_REPORT_SECTION,
  REORDER_REPORT_SECTIONS,
  MOVE_SECTION_UP,
  MOVE_SECTION_DOWN,
  ADD_NEW_SECTION_INPUT,
  REMOVE_NEW_SECTION_INPUT,
  ADD_NEW_SECTION_START,
  ADD_NEW_SECTION_SUCCESS,
  ADD_NEW_SECTION_FAILURE,
  SAVE_NEW_SECTION_START,
  SAVE_NEW_SECTION_SUCCESS,
  SAVE_NEW_SECTION_FAILURE,
  SET_LAST_MESSAGE_TRACE_ID,
  SET_FOLLOWUP_SUGGESTIONS,
  CLEAR_FOLLOWUP_SUGGESTIONS,
  SET_WELCOME_SUGGESTIONS,
  CLEAR_WELCOME_SUGGESTIONS,
  ATTACH_QUERY_PARAMS,
  TURN_STARTED,
  TURN_STATUS,
  TURN_THINKING,
  MESSAGE_DELTA,
  MESSAGE_COMPLETE,
  TURN_FAILED,
  CONFIRMATION_REQUIRED,
  LOAD_TRANSCRIPT,
  SET_ACTIVE_CONVERSATION,
  SET_CONVERSATIONS,
  CONVERSATION_UPSERTED,
  CONVERSATION_REMOVED,
  SET_CONVERSATIONS_LOADING,
  TURN_STOPPED,
  SET_TURN_FEEDBACK,
} from './actionTypes'
import { AI_ASSISTANT_CONSTANTS } from '../constants/aiAssistantConstants'
import { getLogoUrlFromCitation } from '../utils/logoDev'

const MESSAGE_FIELDS = [
  "text",
  "assumption",
  "link",
  "title",
  "description",
  "sources",
  "source_type",
  "grounding_sources",
  "chartConfig",
  "tableConfig",
  "report_id",
  "report_data",
  "report_status",
  "report_status_message",
  "report_title",
  "trace_id",
];

const normalizeMessage = (message = {}) => {
  const payload = { ...(message.payload || {}) };

  MESSAGE_FIELDS.forEach((field) => {
    if (message[field] !== undefined && payload[field] === undefined) {
      payload[field] = message[field];
    }
  });

  const normalized = {
    ...message,
    type: message.type || "Harris",
    message_type: message.message_type || "text",
    payload,
  };

  MESSAGE_FIELDS.forEach((field) => {
    if (normalized[field] === undefined && payload[field] !== undefined) {
      normalized[field] = payload[field];
    }
  });

  return normalized;
};

const normalizeMessages = (messages = []) => messages.map(normalizeMessage);

// The transcript used to be mirrored into localStorage. It is not any more:
// the server is the record, and a browser-local copy was what made the same
// conversation look different on different machines. Normalisation stays,
// because the message shape below is what the components read.
const persistMessages = (messages = []) => normalizeMessages(messages);

/** Rebuilds the turn index after any change to `messages`. */
const indexByTurn = (messages = []) => {
  const index = {};
  messages.forEach((message, i) => {
    if (message?.turn_id && isHarrisText(message)) index[message.turn_id] = i;
  });
  return index;
};

/** The pair of messages one turn renders as. */
const turnMessages = (turn) => [
  {
    id: `${turn.turn_id}:u`,
    turn_id: turn.turn_id,
    type: "You",
    message_type: "text",
    text: turn.user_text ?? "",
    payload: { text: turn.user_text ?? "" },
  },
  {
    id: `${turn.turn_id}:a`,
    turn_id: turn.turn_id,
    type: "Harris",
    message_type: "text",
    text: turn.answer_text ?? "",
    payload: { text: turn.answer_text ?? "" },
    trace_id: turn.trace_id ?? null,
    turn_status: turn.status ?? "running",
    // Comes back from the server as "up"/"down", so a rating survives a
    // reload and is the same on every device the conversation is open on.
    // Normalised here, so the components read one shape whether the rating
    // was loaded with the transcript or just given.
    feedback: turn.feedback ? { rating: turn.feedback } : null,
    ...(turn.error_message ? { error: turn.error_message } : {}),
  },
];

/** Replaces one message in place, keeping every other index stable. */
const replaceAt = (messages, index, next) => [
  ...messages.slice(0, index),
  next,
  ...messages.slice(index + 1),
];

const isHarrisText = (msg) =>
  (msg.type || "").toLowerCase() === "harris" &&
  (msg.message_type || "text") === "text";

const findLastHarrisTextIndex = (messages) => {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (isHarrisText(messages[i])) return i;
  }
  return -1;
};

/**
 * Loosely typed shape of a chat message. Messages carry a large,
 * organically-grown set of optional fields (see MESSAGE_FIELDS above and
 * the report/section payloads below), so this intentionally stays a
 * permissive record rather than an exhaustive union.
 * @typedef {Record<string, unknown>} ChatMessage
 */

/**
 * @typedef {Object} ChatState
 * @property {ChatMessage[]} messages
 * @property {number} lastHarrisTextIndex
 * @property {boolean} isTyping
 * @property {boolean} isAssistantResponding
 * @property {boolean} hasInitializedMessages
 * @property {unknown[]} selectedValues
 * @property {boolean} isChatOpen
 * @property {boolean} isChatExpanded
 * @property {Record<string, unknown>[]} reports
 * @property {string | number | null} activeReportId
 * @property {string} errorMessage
 * @property {unknown} taskStatusMessage
 * @property {Record<string, unknown>[]} citations
 * @property {boolean} isActionControllerVisible
 * @property {boolean} isCitationsExpanded
 * @property {string | null} actionControllerContent
 * @property {unknown} historyMetadata
 * @property {unknown[]} followupSuggestions
 * @property {unknown[]} welcomeSuggestions
 */

/** @type {ChatState} */
const initialState = {
  messages: [],
  lastHarrisTextIndex: -1,
  isTyping: false,
  isAssistantResponding: false,
  hasInitializedMessages: false,
  selectedValues: [],
  isChatOpen: false,
  isChatExpanded: false,
  reports: [],
  activeReportId: null,
  errorMessage: '',
  taskStatusMessage: null,
  citations: [],
  isActionControllerVisible: false,
  isCitationsExpanded: false,
  actionControllerContent: null,
  historyMetadata: JSON.parse(localStorage.getItem("historyMetadata")) || null,
  followupSuggestions: [],
  welcomeSuggestions: [],

  // -- conversations -------------------------------------------------------
  /** Index of `turn_id` to the position of that turn's assistant message.
   *  Replaces `lastHarrisTextIndex` for anything arriving over the wire:
   *  "the last message" is not a stable target once a second device can add
   *  one mid-reply. */
  messagesByTurn: {},
  /** Highest `seq` seen per turn, so a gap is noticed rather than rendered. */
  lastSeqByTurn: {},
  activeTurnId: null,
  activeConversationId: null,
  conversations: [],
  conversationsCursor: null,
  conversationsLoading: false,
  isTranscriptLoading: false,
  pendingConfirmation: null,
  /** The turn whose context panel is open, or null. One at a time: the panel
   *  covers the whole transcript, so a second one would only hide the first. */
  contextPanelTurnId: null,
};

/**
 * Legacy chat reducer, ported as-is from geo-ui. Logic is intentionally
 * untouched; only the state/action signature is annotated so it can be
 * consumed from store.ts without `@ts-ignore`.
 * @param {ChatState} [state]
 * @param {{ type: string, payload?: any }} action
 * @returns {ChatState}
 */
const chatReducer = (state = initialState, action) => {
  switch (action.type) {

    case SET_CONTEXT_PANEL_TURN:
      return { ...state, contextPanelTurnId: action.payload ?? null };

    case TOGGLE_CHAT_VISIBILITY:
      return { ...state, isChatOpen: action.payload };

    case ADD_MESSAGE: {
      const updatedMessages = persistMessages([...state.messages, action.payload]);
      const newMsg = action.payload;
      const newIndex = isHarrisText(newMsg)
        ? updatedMessages.length - 1
        : state.lastHarrisTextIndex;
      return { ...state, messages: updatedMessages, lastHarrisTextIndex: newIndex };
    }

    case ADD_BULK_MESSAGE: {
      const updatedMessages = persistMessages(action.payload);
      return { ...state, messages: updatedMessages, lastHarrisTextIndex: findLastHarrisTextIndex(updatedMessages) };
    }

    case USER_TYPING:
      return { ...state, isTyping: action.payload };

    case ASSISTANT_RESPONDING:
      return { ...state, isAssistantResponding: action.payload };

    case ADD_ERROR_MESSAGE:
      return { ...state, errorMessage: action.payload };

    case CLEAR_ERROR_MESSAGE:
      return { ...state, errorMessage: '' };

    case DELETE_ALL_MESSAGES:
      window.localStorage.removeItem("messages");
      return { ...state, messages: [], lastHarrisTextIndex: -1 };

    case SET_HAS_INITIALIZED_MESSAGES:
      return { ...state, hasInitializedMessages: action.payload };

    case ADD_SELECTED_VALUE:
      return { ...state, selectedValues: [...state.selectedValues, action.payload] };

    case REMOVE_SELECTED_VALUE:
      return {
        ...state,
        selectedValues: state.selectedValues.filter((value) => value !== action.payload),
      };
    case REMOVE_ALL_SELECTED_VALUE:
      return {
        ...state,
        selectedValues: []
      }
    case DELETE_MESSAGES_KEEPING_INITIAL: {
      const keptMessages = persistMessages(state.messages.slice(0, action.payload));
      return {
        ...state,
        messages: keptMessages,
        lastHarrisTextIndex: findLastHarrisTextIndex(keptMessages),
      };
    }
    case APPEND_TO_LAST_MESSAGE: {
      const index = state.lastHarrisTextIndex;
      if (index === -1 || index >= state.messages.length) return state;

      const lastMessage = state.messages[index];
      const nextText =
        (lastMessage.payload?.text || lastMessage.text || "") + action.payload;
      const updatedLastMessage = {
        ...lastMessage,
        text: nextText,
        payload: { ...(lastMessage.payload || {}), text: nextText },
      };
      const updatedMessages = [
        ...state.messages.slice(0, index),
        updatedLastMessage,
        ...state.messages.slice(index + 1),
      ];

      const normalizedMessages = persistMessages(updatedMessages);
      return { ...state, messages: normalizedMessages };
    }
    case ADD_REPORT: {
      const existingReport = state.reports.find(r => r.id === action.payload.id);
      let newReports;
      if (existingReport) {
        newReports = state.reports.map(r => r.id === action.payload.id ? action.payload : r);
      } else {
        newReports = [...state.reports, action.payload];
      }
      localStorage.setItem("ai_reports", JSON.stringify(newReports));
      return { ...state, reports: newReports, activeReportId: action.payload.id };
    }

    case SET_ACTIVE_REPORT:
      return { ...state, activeReportId: action.payload };

    case LOAD_REPORTS:
      return { ...state, reports: action.payload };

    case SET_CHAT_EXPANDED:
      return {
        ...state,
        isChatExpanded: action.payload,
      };
    case SET_TASK_STATUS_MESSAGE:
      return {
        ...state,
        taskStatusMessage: action.payload,
      };
    case SHOW_CITATIONS: {
      const payload = Array.isArray(action.payload) ? action.payload : [];
      const logoToken = AI_ASSISTANT_CONSTANTS.LOGO_DEV_PUBLISHABLE_KEY || '';
      const transformedCitations = payload.map((source) => {
        const link = source.link ?? source.url ?? '';
        const logo =
          source.icon ??
          getLogoUrlFromCitation({ link }, logoToken, 32) ??
          null;
        return {
          title: source.title ?? source.tile ?? '',
          logo,
          date: source.date ?? '',
          link,
        };
      });
      return {
        ...state,
        citations: transformedCitations,
        isActionControllerVisible: true,
        isCitationsExpanded: true,
        actionControllerContent: 'Citations',
      };
    }
    case HIDE_CITATIONS:
      return {
        ...state,
        isActionControllerVisible: false,
        isCitationsExpanded: false,
        actionControllerContent: null,
      };
    case CLEAR_CITATIONS:
      return {
        ...state,
        citations: [],
        isActionControllerVisible: false,
        isCitationsExpanded: false,
        actionControllerContent: null,
      };
    case SET_HISTORY_METADATA:
      localStorage.setItem("historyMetadata", JSON.stringify(action.payload));
      return {
        ...state,
        historyMetadata: action.payload,
      };
    case CLEAR_HISTORY_METADATA:
      localStorage.removeItem("historyMetadata");
      return {
        ...state,
        historyMetadata: null,
      };
    case DELETE_REPORT_SECTION: {
      const { reportId, sectionId } = action.payload;
      const updatedMessages = state.messages.map(msg => {
        if (msg.message_type === 'report' && msg.report_id === reportId) {
          return {
            ...msg,
            report_data: {
              ...msg.report_data,
              sections: msg.report_data.sections.filter(
                section => section.section_id !== sectionId
              ),
            },
          };
        }
        return msg;
      });
      const normalizedMessages = persistMessages(updatedMessages);
      return { ...state, messages: normalizedMessages };
    }
    case SET_FOLLOWUP_SUGGESTIONS:
      return {
        ...state,
        followupSuggestions: Array.isArray(action.payload) ? action.payload : [],
      };
    case CLEAR_FOLLOWUP_SUGGESTIONS:
      return {
        ...state,
        followupSuggestions: [],
      };
    case SET_WELCOME_SUGGESTIONS:
      return {
        ...state,
        welcomeSuggestions: Array.isArray(action.payload) ? action.payload : [],
      };
    case CLEAR_WELCOME_SUGGESTIONS:
      return {
        ...state,
        welcomeSuggestions: [],
      };
    case REORDER_REPORT_SECTIONS: {
      const { reportId, newSections } = action.payload;
      const updatedMessages = state.messages.map(msg => {
        if (msg.message_type === 'report' && msg.report_id === reportId) {
          return {
            ...msg,
            report_data: {
              ...msg.report_data,
              sections: newSections,
            },
          };
        }
        return msg;
      });
      const normalizedMessages = persistMessages(updatedMessages);
      return { ...state, messages: normalizedMessages };
    }
    case MOVE_SECTION_UP: {
      const { reportId, sectionId } = action.payload;
      const updatedMessages = state.messages.map(msg => {
        if (msg.message_type === 'report' && msg.report_id === reportId) {
          const sections = [...msg.report_data.sections];
          const index = sections.findIndex(s => s.section_id === sectionId);
          if (index > 0) {
            const temp = sections[index];
            sections[index] = sections[index - 1];
            sections[index - 1] = temp;
          }
          return {
            ...msg,
            report_data: {
              ...msg.report_data,
              sections,
            },
          };
        }
        return msg;
      });
      const normalizedMessages = persistMessages(updatedMessages);
      return { ...state, messages: normalizedMessages };
    }
    case MOVE_SECTION_DOWN: {
      const { reportId, sectionId } = action.payload;
      const updatedMessages = state.messages.map(msg => {
        if (msg.message_type === 'report' && msg.report_id === reportId) {
          const sections = [...msg.report_data.sections];
          const index = sections.findIndex(s => s.section_id === sectionId);
          if (index < sections.length - 1) {
            const temp = sections[index];
            sections[index] = sections[index + 1];
            sections[index + 1] = temp;
          }
          return {
            ...msg,
            report_data: {
              ...msg.report_data,
              sections,
            },
          };
        }
        return msg;
      });
      const normalizedMessages = persistMessages(updatedMessages);
      return { ...state, messages: normalizedMessages };
    }
    case ADD_NEW_SECTION_INPUT: {
      const { reportId, afterSectionId } = action.payload;
      const updatedMessages = state.messages.map(msg => {
        if (msg.message_type === 'report' && msg.report_id === reportId) {
          const sections = msg.report_data.sections;
          const placeholderExists = sections.some(section =>
            section.type === 'new_section_input' &&
            (section.after_section_id ?? null) === (afterSectionId ?? null)
          );
          if (placeholderExists) {
            return msg;
          }
          const placeholder = {
            type: 'new_section_input',
            after_section_id: afterSectionId ?? null,
            draft_id: null,
            loading: false,
            error: null,
            conversation: [],
            candidate_section: null,
          };
          let hasInserted = false;
          const newSections = sections.reduce((acc, section) => {
            acc.push(section);
            if (!hasInserted && afterSectionId && section.section_id === afterSectionId) {
              acc.push(placeholder);
              hasInserted = true;
            }
            return acc;
          }, []);
          if (!hasInserted) {
            newSections.push(placeholder);
          }
          return {
            ...msg,
            report_data: {
              ...msg.report_data,
              sections: newSections,
            },
          };
        }
        return msg;
      });
      const normalizedMessages = persistMessages(updatedMessages);
      return { ...state, messages: normalizedMessages };
    }
    case REMOVE_NEW_SECTION_INPUT: {
      const { reportId, draftId, afterSectionId } = action.payload;
      const updatedMessages = state.messages.map(msg => {
        if (msg.message_type === 'report' && msg.report_id === reportId) {
          let removed = false;
          const newSections = msg.report_data.sections.filter(section => {
            if (removed || section.type !== 'new_section_input') {
              return true;
            }
            const matches =
              (draftId && section.draft_id === draftId) ||
              (draftId == null && (section.after_section_id ?? null) === (afterSectionId ?? null));
            if (matches) {
              removed = true;
              return false;
            }
            return true;
          });
          return {
            ...msg,
            report_data: {
              ...msg.report_data,
              sections: newSections,
            },
          };
        }
        return msg;
      });
      const normalizedMessages = persistMessages(updatedMessages);
      return { ...state, messages: normalizedMessages };
    }
    case ADD_NEW_SECTION_START: {
      const { reportId, afterSectionId, draftId } = action.payload;
      const updatedMessages = state.messages.map(msg => {
        if (msg.message_type === 'report' && msg.report_id === reportId) {
          const newSections = msg.report_data.sections.map(section => {
            if (section.type !== 'new_section_input') {
              return section;
            }
            const slotMatches = (section.after_section_id ?? null) === (afterSectionId ?? null);
            const hasDraftId = Boolean(section.draft_id);
            const matches =
              (draftId && section.draft_id === draftId) ||
              (!hasDraftId && slotMatches);
            if (matches) {
              return { ...section, loading: true, error: null };
            }
            return section;
          });
          return {
            ...msg,
            report_data: {
              ...msg.report_data,
              sections: newSections,
            },
          };
        }
        return msg;
      });
      const normalizedMessages = persistMessages(updatedMessages);
      return { ...state, messages: normalizedMessages };
    }
    case ADD_NEW_SECTION_SUCCESS: {
      const { reportId, draftId, previousDraftId, candidateSection, afterSectionId, turns } = action.payload;
      const updatedMessages = state.messages.map(msg => {
        if (msg.message_type === 'report' && msg.report_id === reportId) {
          const newSections = msg.report_data.sections.map(section => {
            if (section.type !== 'new_section_input') {
              return section;
            }
            const slotMatches = (section.after_section_id ?? null) === (afterSectionId ?? null);
            const hasDraftId = Boolean(section.draft_id);
            const matches =
              (draftId && section.draft_id === draftId) ||
              (previousDraftId && section.draft_id === previousDraftId) ||
              (!hasDraftId && slotMatches);
            if (matches) {
              return {
                ...section,
                draft_id: draftId ?? section.draft_id,
                loading: false,
                error: null,
                candidate_section: candidateSection,
                conversation: turns,
              };
            }
            return section;
          });
          return {
            ...msg,
            report_data: {
              ...msg.report_data,
              sections: newSections,
            },
          };
        }
        return msg;
      });
      const normalizedMessages = persistMessages(updatedMessages);
      return { ...state, messages: normalizedMessages };
    }

    case ADD_NEW_SECTION_FAILURE: {
      const { reportId, error, afterSectionId, draftId } = action.payload;
      const updatedMessages = state.messages.map(msg => {
        if (msg.message_type === 'report' && msg.report_id === reportId) {
          const newSections = msg.report_data.sections.map(section => {
            if (section.type !== 'new_section_input') {
              return section;
            }
            const slotMatches = (section.after_section_id ?? null) === (afterSectionId ?? null);
            const hasDraftId = Boolean(section.draft_id);
            const matches =
              (draftId && section.draft_id === draftId) ||
              (!hasDraftId && slotMatches);
            if (matches) {
              return { ...section, loading: false, error: error };
            }
            return section;
          });
          return {
            ...msg,
            report_data: {
              ...msg.report_data,
              sections: newSections,
            },
          };
        }
        return msg;
      });
      const normalizedMessages = persistMessages(updatedMessages);
      return { ...state, messages: normalizedMessages };
    }

    case ENTER_SECTION_EDIT_MODE: {
      const { reportId, sectionId } = action.payload;
      const updatedMessages = state.messages.map(msg => {
        if (msg.message_type === 'report' && msg.report_id === reportId) {
          const newSections = msg.report_data.sections.map(section => {
            if (section.section_id === sectionId) {
              if (section.edit_state?.active) {
                return section;
              }
              return {
                ...section,
                edit_state: {
                  active: true,
                  draft_id: section.edit_state?.draft_id ?? null,
                  loading: false,
                  saving: false,
                  error: null,
                  conversation: section.edit_state?.conversation ?? [],
                  candidate_section: section.edit_state?.candidate_section ?? null,
                },
              };
            }
            return section;
          });
          return {
            ...msg,
            report_data: {
              ...msg.report_data,
              sections: newSections,
            },
          };
        }
        return msg;
      });
      const normalizedMessages = persistMessages(updatedMessages);
      return { ...state, messages: normalizedMessages };
    }
    case EXIT_SECTION_EDIT_MODE: {
      const { reportId, sectionId } = action.payload;
      const updatedMessages = state.messages.map(msg => {
        if (msg.message_type === 'report' && msg.report_id === reportId) {
          const newSections = msg.report_data.sections.map(section => {
            if (section.section_id === sectionId && section.edit_state?.active) {
              const { edit_state, ...rest } = section;
              return {
                ...rest,
                edit_state: undefined,
                loading: false,
              };
            }
            return section;
          });
          return {
            ...msg,
            report_data: {
              ...msg.report_data,
              sections: newSections,
            },
          };
        }
        return msg;
      });
      const normalizedMessages = persistMessages(updatedMessages);
      return { ...state, messages: normalizedMessages };
    }
    case REVISE_SECTION_DRAFT_START: {
      const { reportId, sectionId } = action.payload;
      const updatedMessages = state.messages.map(msg => {
        if (msg.message_type === 'report' && msg.report_id === reportId) {
          const newSections = msg.report_data.sections.map(section => {
            if (section.section_id === sectionId && section.edit_state?.active) {
              return {
                ...section,
                edit_state: {
                  ...section.edit_state,
                  loading: true,
                  saving: false,
                  error: null,
                },
              };
            }
            return section;
          });
          return {
            ...msg,
            report_data: {
              ...msg.report_data,
              sections: newSections,
            },
          };
        }
        return msg;
      });
      const normalizedMessages = persistMessages(updatedMessages);
      return { ...state, messages: normalizedMessages };
    }
    case REVISE_SECTION_DRAFT_SUCCESS: {
      const { reportId, sectionId, draftId, candidateSection, turns } = action.payload;
      const updatedMessages = state.messages.map(msg => {
        if (msg.message_type === 'report' && msg.report_id === reportId) {
          const newSections = msg.report_data.sections.map(section => {
            if (section.section_id === sectionId && section.edit_state?.active) {
              return {
                ...section,
                edit_state: {
                  active: true,
                  draft_id: draftId,
                  loading: false,
                  saving: false,
                  error: null,
                  conversation: turns,
                  candidate_section: candidateSection,
                },
              };
            }
            return section;
          });
          return {
            ...msg,
            report_data: {
              ...msg.report_data,
              sections: newSections,
            },
          };
        }
        return msg;
      });
      const normalizedMessages = persistMessages(updatedMessages);
      return { ...state, messages: normalizedMessages };
    }
    case REVISE_SECTION_DRAFT_FAILURE: {
      const { reportId, sectionId, error } = action.payload;
      const updatedMessages = state.messages.map(msg => {
        if (msg.message_type === 'report' && msg.report_id === reportId) {
          const newSections = msg.report_data.sections.map(section => {
            if (section.section_id === sectionId && section.edit_state?.active) {
              return {
                ...section,
                edit_state: {
                  ...section.edit_state,
                  loading: false,
                  saving: false,
                  error: error,
                },
              };
            }
            return section;
          });
          return {
            ...msg,
            report_data: {
              ...msg.report_data,
              sections: newSections,
            },
          };
        }
        return msg;
      });
      const normalizedMessages = persistMessages(updatedMessages);
      return { ...state, messages: normalizedMessages };
    }
    case SAVE_SECTION_EDIT_START: {
      const { reportId, sectionId } = action.payload;
      const updatedMessages = state.messages.map(msg => {
        if (msg.message_type === 'report' && msg.report_id === reportId) {
          const newSections = msg.report_data.sections.map(section => {
            if (section.section_id === sectionId && section.edit_state?.active) {
              return {
                ...section,
                loading: true,
                edit_state: {
                  ...section.edit_state,
                  loading: true,
                  saving: true,
                  error: null,
                },
              };
            }
            return section;
          });
          return {
            ...msg,
            report_data: {
              ...msg.report_data,
              sections: newSections,
            },
          };
        }
        return msg;
      });
      const normalizedMessages = persistMessages(updatedMessages);
      return { ...state, messages: normalizedMessages };
    }
    case SAVE_SECTION_EDIT_SUCCESS: {
      const { reportId, sectionId, updatedSection } = action.payload;
      const updatedMessages = state.messages.map(msg => {
        if (msg.message_type === 'report' && msg.report_id === reportId) {
          const newSections = msg.report_data.sections.map(section => {
            if (section.section_id === sectionId) {
              return {
                ...section,
                ...updatedSection,
                loading: false,
                edit_state: undefined,
              };
            }
            return section;
          });
          const nextMsg = {
            ...msg,
            report_data: {
              ...msg.report_data,
              sections: newSections,
            },
          };
          return nextMsg;
        }
        return msg;
      });
      const normalizedMessages = persistMessages(updatedMessages);
      return { ...state, messages: normalizedMessages };
    }
    case SAVE_SECTION_EDIT_FAILURE: {
      const { reportId, sectionId, error } = action.payload;
      const updatedMessages = state.messages.map(msg => {
        if (msg.message_type === 'report' && msg.report_id === reportId) {
          const newSections = msg.report_data.sections.map(section => {
            if (section.section_id === sectionId && section.edit_state?.active) {
              return {
                ...section,
                loading: false,
                edit_state: {
                  ...section.edit_state,
                  loading: false,
                  saving: false,
                  error: error,
                },
              };
            }
            return section;
          });
          return {
            ...msg,
            report_data: {
              ...msg.report_data,
              sections: newSections,
            },
          };
        }
        return msg;
      });
      const normalizedMessages = persistMessages(updatedMessages);
      return { ...state, messages: normalizedMessages };
    }
    case SAVE_NEW_SECTION_START: {
      const { reportId, draftId, afterSectionId } = action.payload;
      const updatedMessages = state.messages.map(msg => {
        if (msg.message_type === 'report' && msg.report_id === reportId) {
          const newSections = msg.report_data.sections.map(section => {
            if (section.type !== 'new_section_input') {
              return section;
            }
            const matches =
              section.draft_id === draftId ||
              (section.draft_id == null && (section.after_section_id ?? null) === (afterSectionId ?? null));
            if (matches) {
              return { ...section, loading: true, error: null };
            }
            return section;
          });
          return {
            ...msg,
            report_data: {
              ...msg.report_data,
              sections: newSections,
            },
          };
        }
        return msg;
      });
      const normalizedMessages = persistMessages(updatedMessages);
      return { ...state, messages: normalizedMessages };
    }
    case SAVE_NEW_SECTION_SUCCESS: {
      const { reportId, draftId, afterSectionId, newSection } = action.payload;
      const updatedMessages = state.messages.map(msg => {
        if (msg.message_type === 'report' && msg.report_id === reportId) {
          const newSections = msg.report_data.sections.flatMap(section => {
            if (section.type === 'new_section_input') {
              const matches =
                section.draft_id === draftId ||
                (section.draft_id == null && (section.after_section_id ?? null) === (afterSectionId ?? null));
              if (matches) {
                return [newSection];
              }
            }
            return [section];
          });
          return {
            ...msg,
            report_data: {
              ...msg.report_data,
              sections: newSections,
            },
          };
        }
        return msg;
      });
      const normalizedMessages = persistMessages(updatedMessages);
      return { ...state, messages: normalizedMessages };
    }
    case SAVE_NEW_SECTION_FAILURE: {
      const { reportId, draftId, afterSectionId, error } = action.payload;
      const updatedMessages = state.messages.map(msg => {
        if (msg.message_type === 'report' && msg.report_id === reportId) {
          const newSections = msg.report_data.sections.map(section => {
            if (section.type !== 'new_section_input') {
              return section;
            }
            const matches =
              section.draft_id === draftId ||
              (section.draft_id == null && (section.after_section_id ?? null) === (afterSectionId ?? null));
            if (matches) {
              return { ...section, loading: false, error: error };
            }
            return section;
          });
          return {
            ...msg,
            report_data: {
              ...msg.report_data,
              sections: newSections,
            },
          };
        }
        return msg;
      });
      const normalizedMessages = persistMessages(updatedMessages);
      return { ...state, messages: normalizedMessages };
    }
    case SET_TURN_FEEDBACK: {
      // Keyed on the turn, not on a position in the list. Two devices on the
      // same conversation do not agree on positions, and a rating landing on
      // the wrong reply is worse than no rating at all.
      const { turnId, value } = action.payload;
      const index = state.messagesByTurn[turnId];
      if (index === undefined) return state;
      return {
        ...state,
        messages: replaceAt(state.messages, index, {
          ...state.messages[index],
          feedback: value ? { rating: value } : null,
        }),
      };
    }
    case SET_LAST_MESSAGE_TRACE_ID: {
      const traceId = action.payload;
      const index = state.lastHarrisTextIndex;
      if (typeof traceId !== "string" || !traceId || index < 0 || index >= state.messages.length) {
        return state;
      }
      const updatedMessages = state.messages.map((msg, i) =>
        i === index ? { ...msg, trace_id: traceId } : msg,
      );
      const normalizedMessages = persistMessages(updatedMessages);
      return { ...state, messages: normalizedMessages };
    }
    case UPDATE_REPORT_MESSAGE: {
      const { report_id, updatedData } = action.payload;
      const updatedMessages = state.messages.map(msg => {
        if (msg.report_id === report_id) {
          const nextReportId = updatedData.report_id ?? msg.report_id;
          const nextPayload = {
            ...(msg.payload || {}),
            report_id: nextReportId,
            report_status: updatedData.report_status ?? msg.report_status,
            report_status_message: updatedData.report_status_message ?? msg.report_status_message,
            report_title: updatedData.report_title ?? msg.report_title,
            report_data: updatedData.report_data ?? msg.report_data,
          };
          return {
            ...msg,
            report_id: nextReportId,
            report_status: updatedData.report_status ?? msg.report_status,
            report_status_message: updatedData.report_status_message ?? msg.report_status_message,
            report_title: updatedData.report_title ?? msg.report_title,
            report_data: updatedData.report_data ?? msg.report_data,
            payload: nextPayload,
          };
        }
        return msg;
      });
      const normalizedMessages = persistMessages(updatedMessages);
      return { ...state, messages: normalizedMessages };
    }

    case ATTACH_QUERY_PARAMS: {
      const index = state.lastHarrisTextIndex;
      if (index === -1 || index >= state.messages.length) return state;

      const lastMessage = state.messages[index];
      const updatedLastMessage = {
        ...lastMessage,
        payload: {
          ...(lastMessage.payload || {}),
          query_params_data: action.payload.data,
        },
        query_params_data: action.payload.data,
      };

      const updatedMessages = [
        ...state.messages.slice(0, index),
        updatedLastMessage,
        ...state.messages.slice(index + 1),
      ];

      const normalizedMessages = persistMessages(updatedMessages);
      return { ...state, messages: normalizedMessages };
    }

    // ── Conversations (contract 1.4) ────────────────────────────────────
    case TURN_STARTED: {
      const { turnId, userText, turnSeq } = action.payload;
      // Idempotent by design. The page that sent the message draws the turn
      // straight away; the same command then arrives over the channel, where
      // it is what tells every *other* page to draw it. One path, and the
      // sender recognises its own.
      if (state.messagesByTurn[turnId] !== undefined) return state;
      console.debug("[Lou] turn opened", turnId);

      const messages = persistMessages([
        ...state.messages,
        ...turnMessages({ turn_id: turnId, user_text: userText, status: "running" }),
      ]);
      return {
        ...state,
        messages,
        messagesByTurn: indexByTurn(messages),
        lastHarrisTextIndex: messages.length - 1,
        activeTurnId: turnId,
        isAssistantResponding: true,
        errorMessage: "",
        pendingConfirmation: null,
        lastSeqByTurn: { ...state.lastSeqByTurn, [turnId]: turnSeq ?? 0 },
      };
    }

    case TURN_STATUS:
      return {
        ...state,
        taskStatusMessage: {
          type: "task_status",
          taskTitle: action.payload.phase,
          taskMessage: action.payload.phase,
        },
      };

    case TURN_THINKING: {
      const index = state.messagesByTurn[action.payload.turnId];
      if (index === undefined) return state;
      const message = state.messages[index];
      const thinking = (message.thinking || "") + action.payload.text;
      return {
        ...state,
        messages: replaceAt(state.messages, index, { ...message, thinking }),
      };
    }

    case MESSAGE_DELTA: {
      const index = state.messagesByTurn[action.payload.turnId];
      if (index === undefined) {
        // The turn has no bubble on screen, so there is nothing to append to
        // and the text is lost. Means TURN_STARTED never ran for this id.
        console.warn(
          "[Lou] delta for an unknown turn - dropped",
          action.payload.turnId,
          "known:",
          Object.keys(state.messagesByTurn),
        );
        return state;
      }
      const message = state.messages[index];
      const text = (message.payload?.text || message.text || "") + action.payload.text;
      return {
        ...state,
        messages: replaceAt(state.messages, index, {
          ...message,
          text,
          payload: { ...(message.payload || {}), text },
        }),
      };
    }

    case MESSAGE_COMPLETE: {
      const { turnId, text, mode, traceId, suggestions } = action.payload;
      const index = state.messagesByTurn[turnId];
      if (index === undefined) {
        console.warn(
          "[Lou] completion for an unknown turn - dropped",
          turnId,
          "known:",
          Object.keys(state.messagesByTurn),
        );
        return state;
      }
      const message = state.messages[index];
      // Replace, never append. The deltas were a preview; this is the reply.
      // That is what makes one lost while the connection dropped harmless.
      const messages = replaceAt(state.messages, index, {
        ...message,
        text,
        payload: { ...(message.payload || {}), text },
        turn_status: "done",
        mode: mode ?? message.mode,
        trace_id: traceId ?? message.trace_id,
      });
      return {
        ...state,
        messages,
        activeTurnId: state.activeTurnId === turnId ? null : state.activeTurnId,
        isAssistantResponding:
          state.activeTurnId === turnId ? false : state.isAssistantResponding,
        taskStatusMessage: null,
        followupSuggestions: suggestions?.length
          ? suggestions
          : state.followupSuggestions,
      };
    }

    case TURN_FAILED: {
      const { turnId, message: errorText } = action.payload;
      const index = state.messagesByTurn[turnId];
      const messages =
        index === undefined
          ? state.messages
          : replaceAt(state.messages, index, {
              ...state.messages[index],
              turn_status: "failed",
              error: errorText,
            });
      return {
        ...state,
        messages,
        activeTurnId: state.activeTurnId === turnId ? null : state.activeTurnId,
        isAssistantResponding:
          state.activeTurnId === turnId ? false : state.isAssistantResponding,
        taskStatusMessage: null,
        errorMessage: errorText,
      };
    }

    case TURN_STOPPED: {
      // Whatever had streamed in stays on screen. A stopped turn is a short
      // answer, not a discarded one - the work it already did was real, and
      // blanking it would suggest otherwise.
      const { turnId, text } = action.payload;
      const index = state.messagesByTurn[turnId];
      const messages =
        index === undefined
          ? state.messages
          : replaceAt(state.messages, index, {
              ...state.messages[index],
              ...(typeof text === "string" && text
                ? {
                    text,
                    payload: { ...(state.messages[index].payload || {}), text },
                  }
                : {}),
              turn_status: "stopped",
            });
      return {
        ...state,
        messages,
        activeTurnId: state.activeTurnId === turnId ? null : state.activeTurnId,
        isAssistantResponding:
          state.activeTurnId === turnId ? false : state.isAssistantResponding,
        taskStatusMessage: null,
      };
    }

    case CONFIRMATION_REQUIRED: {
      const { turnId, estimate, message: prompt } = action.payload;
      return {
        ...state,
        pendingConfirmation: { turnId, estimate, message: prompt },
        activeTurnId: state.activeTurnId === turnId ? null : state.activeTurnId,
        isAssistantResponding:
          state.activeTurnId === turnId ? false : state.isAssistantResponding,
        taskStatusMessage: null,
      };
    }

    case LOAD_TRANSCRIPT: {
      // The whole transcript, from the server. Nothing merges with what was
      // on screen: this is a conversation being opened, not extended.
      const turns = action.payload.turns || [];
      const messages = persistMessages(turns.flatMap(turnMessages));
      const running = turns.find((turn) => turn.status === "running");
      return {
        ...state,
        messages,
        messagesByTurn: indexByTurn(messages),
        lastHarrisTextIndex: findLastHarrisTextIndex(messages),
        lastSeqByTurn: {},
        // A turn still running was picked up by the server, not this page.
        // Showing it as in progress is correct: the reply is still coming,
        // and it will arrive on the channel.
        activeTurnId: running ? running.turn_id : null,
        isAssistantResponding: Boolean(running),
        isTranscriptLoading: false,
        errorMessage: "",
        pendingConfirmation: null,
        taskStatusMessage: null,
        hasInitializedMessages: true,
      };
    }

    case SET_ACTIVE_CONVERSATION:
      return {
        ...state,
        activeConversationId: action.payload.conversationId,
        isTranscriptLoading: Boolean(action.payload.loading),
        ...(action.payload.conversationId === state.activeConversationId
          ? {}
          : {
              messages: [],
              messagesByTurn: {},
              lastSeqByTurn: {},
              lastHarrisTextIndex: -1,
              activeTurnId: null,
              isAssistantResponding: false,
              taskStatusMessage: null,
              errorMessage: "",
              pendingConfirmation: null,
              followupSuggestions: [],
            }),
      };

    case SET_CONVERSATIONS_LOADING:
      return { ...state, conversationsLoading: action.payload };

    case SET_CONVERSATIONS: {
      const { conversations, cursor, append } = action.payload;
      return {
        ...state,
        conversations: append
          ? [...state.conversations, ...conversations]
          : conversations,
        conversationsCursor: cursor ?? null,
        conversationsLoading: false,
      };
    }

    case CONVERSATION_UPSERTED: {
      const incoming = action.payload;
      const existing = state.conversations.find((c) => c.id === incoming.id);
      const merged = { ...(existing || {}), ...incoming };
      const others = state.conversations.filter((c) => c.id !== incoming.id);
      // Re-sorted on every change because the list is ordered by recency and
      // a reply anywhere moves that conversation to the top — including one
      // being answered on another device.
      const conversations = [merged, ...others].sort(
        (a, b) =>
          new Date(b.updated_at || 0).getTime() -
          new Date(a.updated_at || 0).getTime(),
      );
      return { ...state, conversations };
    }

    case CONVERSATION_REMOVED:
      return {
        ...state,
        conversations: state.conversations.filter(
          (c) => c.id !== action.payload.conversationId,
        ),
      };

    default:
      return state;
  }
};

export default chatReducer;
